import { ReactNode } from 'react';
import { cn } from '../../../lib/utils';

export interface HorizontalGridProps {
  children: ReactNode;
  className?: string;
  minCardWidth?: string;
  gap?: string | number;
  flexValues?: Array<number | string>;
}

export const HorizontalGrid = ({
  children,
  className = '',
  minCardWidth = '',
  gap = 6,
  flexValues,
}: HorizontalGridProps) => {
  const childArray = Array.isArray(children) ? children : [children];
  const gapClass = typeof gap === 'number' ? `gap-${gap}` : '';
  const styleGap = typeof gap === 'string' ? { gap } : {};

  return (
    <div className={cn('w-full flex flex-row', gapClass, className)} style={styleGap}>
      {childArray.map((child, idx) => {
        const rawFlex = flexValues && flexValues[idx] !== undefined ? flexValues[idx] : 1;

        // Compute style per child:
        const childStyle: React.CSSProperties = {
          display: 'flex',
          flexDirection: 'column',
        };

        // If user passed a percentage string -> fixed width (no flex grow)
        if (typeof rawFlex === 'string' && rawFlex.trim().endsWith('%')) {
          childStyle.flex = `0 0 ${rawFlex.trim()}`;
          childStyle.maxWidth = rawFlex.trim();
        } else if (typeof rawFlex === 'string' && rawFlex === 'auto') {
          childStyle.flex = '0 0 auto';
        } else if (typeof rawFlex === 'string') {
          // strings like 'min-content' or '200px'
          // if it looks like px, use fixed basis; else pass through as flex-basis
          if (/^\d+px$/.test(rawFlex.trim())) {
            childStyle.flex = `0 0 ${rawFlex.trim()}`;
            childStyle.maxWidth = rawFlex.trim();
          } else {
            childStyle.flex = `0 0 ${rawFlex.trim()}`;
          }
        } else if (typeof rawFlex === 'number') {
          // proportional flex (default behaviour)
          // use grow = rawFlex, shrink = 1, basis = 0 to distribute remaining space proportionally
          childStyle.flex = `${rawFlex} 1 0`;
        } else {
          // fallback to equal share
          childStyle.flex = '1 1 0';
        }

        // Respect minCardWidth if provided
        if (minCardWidth) {
          // if a fixed percentage was used, skip setting minWidth
          if (!(typeof rawFlex === 'string' && rawFlex.trim().endsWith('%'))) {
            childStyle.minWidth = minCardWidth;
          }
        }

        return (
          <div key={idx} style={childStyle}>
            {child}
          </div>
        );
      })}
    </div>
  );
};
